import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { parseJSONSafe } from '@shared/utils';
import { CalendarioEstabelecimento, CalendarioService } from '@features/frequencia/services/calendario.service';
import { MobileContextService } from '@core/context/services/mobile-context.service';
import { NotificationService } from '@app/core/services/notification.service';

import {
  InstrumentoArquivoAnexo,
  InstrumentoAvaliacaoRegistro,
  InstrumentoConfiguracaoEstabelecimento,
  InstrumentoPeriodoOption
} from '../services/instrumento-avaliacao.service';
import { InstrumentosAvaliacaoFacadeService } from '../services/instrumentos-avaliacao-facade.service';
import { InstrumentoAvaliacaoModalComponent } from '../components/instrumento-avaliacao-modal.component';
import {
  INSTRUMENTO_ANEXO_EXTENSOES_PERMITIDAS,
  INSTRUMENTO_ANEXO_TAMANHO_MAXIMO,
  INSTRUMENTO_CAMPOS_CRITICOS,
  INSTRUMENTO_CAMPOS_FORMULARIO,
  InstrumentoAlteracaoDetalhada,
  InstrumentoArquivoState,
  InstrumentoCampoAdicionalConfig,
  InstrumentoFormularioAba,
  InstrumentoFormularioState,
  InstrumentoItemEducacional,
  InstrumentoRecuperacaoVinculadaState,
  InstrumentoResumoView,
  InstrumentoTurmaOption,
  TipoInstrumentoAvaliacaoId
} from './instrumento-avaliacao.types';
import {
  getInstrumentoAnexoExtensao,
  mapInstrumentoArquivo,
  mapInstrumentoArquivos,
  mapInstrumentoToForm,
  parseDate,
  parseOptionalNumber,
  resolveInstrumentoArquivoIdentificador,
  serializeInstrumentoArquivos,
  serializeInstrumentoCampoAdicional,
  serializeInstrumentoForm,
  toNullableNumber
} from '../utils/instrumento-avaliacao.utils';

type InstrumentoCampoNumerico = 'notaMinima' | 'notaMaxima' | 'peso';

/**
 * Lógica de CRUD de instrumento de avaliação byte-idêntica entre DesempenhoEscolarComponent
 * e InstrumentosAvaliacaoComponent. Comportamentos divergentes (limites de nota, bloqueio de
 * publicação, etc.) ficam em cada componente concreto; membros divergentes usados aqui são
 * declarados abstratos para o TypeScript garantir que a subclasse os forneça.
 */
export abstract class InstrumentoAvaliacaoHostBase {
  // === Dependências injetadas pelas subclasses (via constructor) ===
  protected abstract calendarioService: CalendarioService;
  protected abstract notificationService: NotificationService;
  protected abstract mobileContextService: MobileContextService;
  // Tipado estruturalmente: cada tela tem sua própria classe concreta InstrumentoAvaliacaoService
  // (dependências privadas distintas no construtor), incompatíveis nominalmente entre si.
  protected abstract instrumentoAvaliacaoService: { get(id: number): Observable<InstrumentoAvaliacaoRegistro> };
  protected abstract destroy$: Subject<void>;

  // === Contexto acadêmico atual (tipos concretos divergem por tela) ===
  protected abstract turma: { id: number } | null;
  protected abstract itemEducacional: { id: number } | null;
  protected abstract periodo: { id: number; dataInicial?: string; dataFinal?: string } | null;

  // === Membros do host divergentes/exclusivos, usados pelos métodos abaixo ===
  protected abstract instrumentoModalRef?: InstrumentoAvaliacaoModalComponent;
  protected abstract instrumentoPeriodoSelecionado: { dataInicial?: string; dataFinal?: string } | null;
  protected abstract instrumentoTurmaEAtividade: boolean;
  protected abstract instrumentoPesoMinimoLimite: number;
  protected abstract instrumentoPesoMaximoLimite: number;
  protected abstract extractTurmaIdFromRaw(raw: Record<string, any>): number | null;
  protected abstract getTipoInstrumentoLabel(tipo: TipoInstrumentoAvaliacaoId | string | null | undefined): string;
  protected abstract sanitizeInstrumentoCampoNumerico(value: unknown, campo?: InstrumentoCampoNumerico): string;

  constructor(protected instrumentosFacade: InstrumentosAvaliacaoFacadeService) {}

  // === Delegação para a facade (estado do modal de instrumento) ===
  get instrumentoFormularioAberto(): boolean { return this.instrumentosFacade.instrumentoFormularioAberto; }
  set instrumentoFormularioAberto(value: boolean) { this.instrumentosFacade.instrumentoFormularioAberto = value; }
  get instrumentoAbaAtiva(): InstrumentoFormularioAba { return this.instrumentosFacade.instrumentoAbaAtiva; }
  set instrumentoAbaAtiva(value: InstrumentoFormularioAba) { this.instrumentosFacade.instrumentoAbaAtiva = value; }
  get instrumentoSalvando(): boolean { return this.instrumentosFacade.instrumentoSalvando; }
  set instrumentoSalvando(value: boolean) { this.instrumentosFacade.instrumentoSalvando = value; }
  get instrumentoCarregando(): boolean { return this.instrumentosFacade.instrumentoCarregando; }
  set instrumentoCarregando(value: boolean) { this.instrumentosFacade.instrumentoCarregando = value; }
  get instrumentoPublicandoId(): number | null { return this.instrumentosFacade.instrumentoPublicandoId; }
  set instrumentoPublicandoId(value: number | null) { this.instrumentosFacade.instrumentoPublicandoId = value; }
  get instrumentoMostrarErros(): boolean { return this.instrumentosFacade.instrumentoMostrarErros; }
  set instrumentoMostrarErros(value: boolean) { this.instrumentosFacade.instrumentoMostrarErros = value; }
  get instrumentoDescricoesRecentes(): Array<{ descricao: string }> { return this.instrumentosFacade.instrumentoDescricoesRecentes; }
  set instrumentoDescricoesRecentes(value: Array<{ descricao: string }>) { this.instrumentosFacade.instrumentoDescricoesRecentes = value; }
  get instrumentoPeriodos(): InstrumentoPeriodoOption[] { return this.instrumentosFacade.instrumentoPeriodos; }
  set instrumentoPeriodos(value: InstrumentoPeriodoOption[]) { this.instrumentosFacade.instrumentoPeriodos = value; }
  get instrumentoTurmasDisponiveis(): InstrumentoTurmaOption[] { return this.instrumentosFacade.instrumentoTurmasDisponiveis; }
  set instrumentoTurmasDisponiveis(value: InstrumentoTurmaOption[]) { this.instrumentosFacade.instrumentoTurmasDisponiveis = value; }
  get instrumentoItensDisponiveis(): InstrumentoItemEducacional[] { return this.instrumentosFacade.instrumentoItensDisponiveis; }
  set instrumentoItensDisponiveis(value: InstrumentoItemEducacional[]) { this.instrumentosFacade.instrumentoItensDisponiveis = value; }
  get instrumentoContextoRegistros(): InstrumentoAvaliacaoRegistro[] { return this.instrumentosFacade.instrumentoContextoRegistros; }
  set instrumentoContextoRegistros(value: InstrumentoAvaliacaoRegistro[]) { this.instrumentosFacade.instrumentoContextoRegistros = value; }
  get instrumentosTelaRegistros(): InstrumentoAvaliacaoRegistro[] { return this.instrumentosFacade.instrumentosTelaRegistros; }
  set instrumentosTelaRegistros(value: InstrumentoAvaliacaoRegistro[]) { this.instrumentosFacade.instrumentosTelaRegistros = value; }
  get instrumentoCalendario(): CalendarioEstabelecimento | null { return this.instrumentosFacade.instrumentoCalendario; }
  set instrumentoCalendario(value: CalendarioEstabelecimento | null) { this.instrumentosFacade.instrumentoCalendario = value; }
  get instrumentoConfiguracaoEstabelecimento(): InstrumentoConfiguracaoEstabelecimento | null { return this.instrumentosFacade.instrumentoConfiguracaoEstabelecimento; }
  set instrumentoConfiguracaoEstabelecimento(value: InstrumentoConfiguracaoEstabelecimento | null) { this.instrumentosFacade.instrumentoConfiguracaoEstabelecimento = value; }
  get instrumentoConfiguracaoTurma(): Record<string, unknown> | null { return this.instrumentosFacade.instrumentoConfiguracaoTurma; }
  set instrumentoConfiguracaoTurma(value: Record<string, unknown> | null) { this.instrumentosFacade.instrumentoConfiguracaoTurma = value; }
  get instrumentoEncerramentosTurma(): Array<Record<string, unknown>> { return this.instrumentosFacade.instrumentoEncerramentosTurma; }
  set instrumentoEncerramentosTurma(value: Array<Record<string, unknown>>) { this.instrumentosFacade.instrumentoEncerramentosTurma = value; }
  get instrumentoForm(): InstrumentoFormularioState { return this.instrumentosFacade.instrumentoForm; }
  set instrumentoForm(value: InstrumentoFormularioState) { this.instrumentosFacade.instrumentoForm = value; }
  get instrumentoArquivos(): InstrumentoArquivoState[] { return this.instrumentosFacade.instrumentoArquivos; }
  set instrumentoArquivos(value: InstrumentoArquivoState[]) { this.instrumentosFacade.instrumentoArquivos = value; }
  get instrumentoCampoAdicional(): InstrumentoCampoAdicionalConfig { return this.instrumentosFacade.instrumentoCampoAdicional; }
  set instrumentoCampoAdicional(value: InstrumentoCampoAdicionalConfig) { this.instrumentosFacade.instrumentoCampoAdicional = value; }
  get instrumentoCamposAdicionaisCarregando(): boolean { return this.instrumentosFacade.instrumentoCamposAdicionaisCarregando; }
  set instrumentoCamposAdicionaisCarregando(value: boolean) { this.instrumentosFacade.instrumentoCamposAdicionaisCarregando = value; }
  get instrumentoCampoAdicionalTemConteudo(): boolean {
    const config = this.instrumentoCampoAdicional;
    if (!config) { return false; }
    if (Array.isArray(config)) { return config.length > 0; }
    return true;
  }
  get instrumentoAnexosEnviando(): boolean { return this.instrumentosFacade.instrumentoAnexosEnviando; }
  set instrumentoAnexosEnviando(value: boolean) { this.instrumentosFacade.instrumentoAnexosEnviando = value; }
  get instrumentoConfirmacao() { return this.instrumentosFacade.instrumentoConfirmacao; }
  set instrumentoConfirmacao(value: { aberta: boolean; tipo: 'excluir' | 'publicacao'; instrumento: InstrumentoResumoView | null; publicar: boolean; titulo: string; confirmadoExclusao: boolean }) { this.instrumentosFacade.instrumentoConfirmacao = value; }
  get instrumentoRecuperacaoVinculada(): InstrumentoRecuperacaoVinculadaState { return this.instrumentosFacade.instrumentoRecuperacaoVinculada; }
  set instrumentoRecuperacaoVinculada(value: InstrumentoRecuperacaoVinculadaState) { this.instrumentosFacade.instrumentoRecuperacaoVinculada = value; }

  // === Snapshots / estado interno (idênticos nos dois componentes) ===
  protected instrumentoSnapshotInicial = '';
  protected instrumentoArquivosSnapshotInicial = '[]';
  protected instrumentoCampoAdicionalSnapshotInicial = 'null';
  protected instrumentoRegistroOriginal: InstrumentoAvaliacaoRegistro | null = null;
  protected readonly instrumentoRecuperacaoDetalhes = new Map<number, InstrumentoAvaliacaoRegistro>();
  protected instrumentoCamposAdicionaisCarregadoParaId: number | null = null;
  protected instrumentoCamposAdicionaisTemplateCarregado = false;
  protected instrumentoCamposAdicionaisObrigatoriosValidos = true;

  // === Mapeamento / serialização de formulário ===
  protected mapInstrumentoToForm(registro: InstrumentoAvaliacaoRegistro): InstrumentoFormularioState {
    return mapInstrumentoToForm(registro, {
      turmaId: this.turma?.id || null,
      itemEducacionalId: this.itemEducacional?.id || null,
      periodoAvaliativoId: this.periodo?.id || null
    });
  }

  protected mapInstrumentoArquivos(arquivos: InstrumentoArquivoAnexo[] | null | undefined): InstrumentoArquivoState[] {
    return mapInstrumentoArquivos(arquivos);
  }

  protected mapInstrumentoArquivo(arquivo: InstrumentoArquivoAnexo, fallbackNome?: string, fallbackTamanho?: number, fallbackTipo?: string): InstrumentoArquivoState {
    return mapInstrumentoArquivo(arquivo, fallbackNome, fallbackTamanho, fallbackTipo);
  }

  protected resolveInstrumentoArquivoIdentificador(anexo: InstrumentoArquivoState, index: number): number | string {
    return resolveInstrumentoArquivoIdentificador(anexo, index);
  }

  protected toNullableNumber(value: number | string | null): number | null {
    return toNullableNumber(value);
  }

  protected serializeInstrumentoForm(): string {
    return serializeInstrumentoForm(this.instrumentoForm);
  }

  protected serializeInstrumentoArquivos(): string {
    return serializeInstrumentoArquivos(this.instrumentoArquivos);
  }

  protected serializeInstrumentoCampoAdicional(): string {
    return serializeInstrumentoCampoAdicional(this.instrumentoCampoAdicional);
  }

  protected normalizarPayloadRecuperacaoParalela(payload: InstrumentoAvaliacaoRegistro): void {
    if (payload.tipoInstrumento !== 'RECUPERACAO_PARALELA') {
      return;
    }

    delete (payload as any).tipo;

    payload.conteudo = this.normalizeOptionalText(payload.conteudo);
    payload.criteriosAvaliativos = this.normalizeOptionalText(payload.criteriosAvaliativos);

    const campoAdicional = (payload as any).campoAdicional;
    if (!Array.isArray(campoAdicional) || campoAdicional.length === 0) {
      delete (payload as any).campoAdicional;
    }

    const instrumentoRecuperacao = (payload as any).instrumentoRecuperacao as Record<string, unknown> | null;
    if (!instrumentoRecuperacao) {
      return;
    }

    delete instrumentoRecuperacao['tipo'];

    const campoAdicionalRecuperacao = instrumentoRecuperacao['campoAdicional'];
    if (!Array.isArray(campoAdicionalRecuperacao) || campoAdicionalRecuperacao.length === 0) {
      delete instrumentoRecuperacao['campoAdicional'];
    }
  }

  protected normalizeOptionalText(value: unknown): string | null {
    const normalized = String(value || '').trim();
    return normalized ? normalized : null;
  }

  protected getAlteracoesCriticasInstrumento(): string[] {
    const original = parseJSONSafe<InstrumentoFormularioState>(this.instrumentoSnapshotInicial, this.instrumentoForm);
    return INSTRUMENTO_CAMPOS_CRITICOS
      .filter(({ campo }) => (original?.[campo] ?? null) !== (this.instrumentoForm[campo] ?? null))
      .map(({ label }) => label);
  }

  protected getAlteracoesFormularioInstrumento(): InstrumentoAlteracaoDetalhada[] {
    const original = parseJSONSafe<InstrumentoFormularioState>(this.instrumentoSnapshotInicial, this.instrumentoForm);
    return INSTRUMENTO_CAMPOS_FORMULARIO
      .filter(({ campo }) => (original?.[campo] ?? null) !== (this.instrumentoForm[campo] ?? null));
  }

  // === Recuperação paralela vinculada ===
  protected deveAtualizarRecuperacaoVinculada(payload: InstrumentoAvaliacaoRegistro, recuperacaoVinculada?: InstrumentoResumoView | null): boolean {
    if (!payload.id || payload.tipoInstrumento !== 'INSTRUMENTO_AVALIACAO' || !recuperacaoVinculada) {
      return false;
    }

    const original = parseJSONSafe<InstrumentoFormularioState>(this.instrumentoSnapshotInicial, this.instrumentoForm);
    const dataOriginal = this.parseDate(original?.data || null);
    const dataAtual = this.parseDate(payload.data);
    const periodoOriginal = original?.periodoAvaliativoId || null;
    const periodoAtual = this.instrumentoForm.periodoAvaliativoId || null;

    return !!((dataOriginal && dataAtual && dataAtual.getTime() > dataOriginal.getTime()) || periodoOriginal !== periodoAtual);
  }

  protected buildRecuperacaoVinculadaEditable(recuperacaoRegistro: InstrumentoAvaliacaoRegistro, instrumentoBase: InstrumentoAvaliacaoRegistro): InstrumentoAvaliacaoRegistro {
    const recuperacao = JSON.parse(JSON.stringify(recuperacaoRegistro)) as InstrumentoAvaliacaoRegistro;
    recuperacao.instrumentoRecuperacao = instrumentoBase;
    recuperacao.periodoAvaliativo = instrumentoBase.periodoAvaliativo || null;

    const dataBase = this.parseDate(instrumentoBase.data);
    const dataRecuperacao = this.parseDate(recuperacao.data);
    if (dataBase && (!dataRecuperacao || dataRecuperacao.getTime() <= dataBase.getTime())) {
      dataBase.setDate(dataBase.getDate() + 1);
      recuperacao.data = dataBase.toISOString().split('T')[0];
    }

    return recuperacao;
  }

  protected buildRecuperacaoVinculadaPayload(recuperacaoRegistro: InstrumentoAvaliacaoRegistro, instrumentoBase: InstrumentoAvaliacaoRegistro): InstrumentoAvaliacaoRegistro {
    const payload = JSON.parse(JSON.stringify(recuperacaoRegistro)) as InstrumentoAvaliacaoRegistro;
    payload.instrumentoRecuperacao = instrumentoBase;
    payload.periodoAvaliativo = instrumentoBase.periodoAvaliativo || null;
    return payload;
  }

  protected validarRecuperacaoVinculada(): string[] {
    const erros: string[] = [];
    const instrumentoBase = this.instrumentoRecuperacaoVinculada.instrumentoBase as any;
    const instrumentoRecuperacao = this.instrumentoRecuperacaoVinculada.instrumentoRecuperacao as any;
    const dataRecuperacao = this.parseDate(instrumentoRecuperacao?.data);
    const dataBase = this.parseDate(instrumentoBase?.data);
    const periodoLetivo = this.getPeriodoSelecionadoParaValidacaoLetivaRecuperacaoVinculada();

    if (!instrumentoRecuperacao) return ['Carregue a recuperação paralela vinculada para continuar.'];
    if (!String(instrumentoRecuperacao.descricao || '').trim()) erros.push('Informe a descrição da recuperação paralela.');
    if (!String(instrumentoRecuperacao.sigla || '').trim()) erros.push('Informe a sigla da recuperação paralela.');
    if (!instrumentoRecuperacao.data) erros.push('Informe a data da recuperação paralela.');
    if (String(instrumentoRecuperacao.descricao || '').trim().length > 100) erros.push('A descrição da recuperação paralela deve ter no máximo 100 caracteres.');
    if (String(instrumentoRecuperacao.sigla || '').trim().length > 5) erros.push('A sigla da recuperação paralela deve ter no máximo 5 caracteres.');
    if (dataBase && dataRecuperacao && dataRecuperacao <= dataBase) erros.push('A data da recuperação paralela deve ser maior que a data do instrumento recuperado.');

    if (dataRecuperacao && periodoLetivo && !this.calendarioService.verificaDiaLetivo(dataRecuperacao, this.instrumentoCalendario, periodoLetivo, undefined)) {
      erros.push('A data da recuperação paralela deve ser um dia letivo.');
    }

    if (dataRecuperacao && !this.instrumentoTurmaEAtividade) {
      const inicio = this.parseDate(periodoLetivo?.dataInicial);
      const fim = this.parseDate(periodoLetivo?.dataFinal);
      if (inicio && fim) {
        fim.setHours(23, 59, 59, 999);
        if (dataRecuperacao < inicio || dataRecuperacao > fim) {
          erros.push('A data da recuperação paralela deve pertencer ao período avaliativo do instrumento recuperado.');
        }
      }
    }

    return Array.from(new Set(erros));
  }

  protected getAnoLetivoAtual(): number | null {
    const contexto = this.mobileContextService.getContextoAtual() as any;
    const ano = Number(contexto?.anoLetivo?.ano ?? contexto?.anoLetivo?.id ?? null);
    return Number.isFinite(ano) ? ano : null;
  }

  protected getRangePeriodoSelecionado(): { inicio: Date; fim: Date } | null {
    const periodoDetalhado = this.findInstrumentoPeriodoDetalhado(this.instrumentoForm.periodoAvaliativoId);
    const dataInicial = periodoDetalhado?.dataInicial || periodoDetalhado?.periodoAvaliativo?.dataInicial || this.periodo?.dataInicial;
    const dataFinal = periodoDetalhado?.dataFinal || periodoDetalhado?.periodoAvaliativo?.dataFinal || this.periodo?.dataFinal;
    const inicio = this.parseDate(dataInicial);
    const fim = this.parseDate(dataFinal);
    if (!inicio || !fim) {
      return null;
    }
    fim.setHours(23, 59, 59, 999);
    return { inicio, fim };
  }

  // === Campos numéricos (nota/peso) ===
  onInstrumentoCampoNumericoKeyDown(event: KeyboardEvent): void {
    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'Enter', 'Escape',
      'ArrowLeft', 'ArrowRight', 'Home', 'End'
    ];
    if (allowedKeys.includes(event.key)) {
      return;
    }

    if ((event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase())) {
      return;
    }

    if (/^\d$/.test(event.key)) {
      return;
    }

    if (event.key === '.' || event.key === ',') {
      return;
    }

    event.preventDefault();
  }

  onInstrumentoCampoNumericoChange(campo: InstrumentoCampoNumerico, value: unknown): void {
    this.instrumentoForm[campo] = this.sanitizeInstrumentoCampoNumerico(value, campo);
  }

  onInstrumentoCampoNumericoBlur(campo: InstrumentoCampoNumerico): void {
    const valor = this.parseOptionalNumber(this.instrumentoForm[campo]);
    if (valor === null) {
      return;
    }

    let isValido = true;
    let mensagem = '';

    if (campo === 'peso') {
      const pesoMinimo = this.instrumentoPesoMinimoLimite;
      const pesoMaximo = this.instrumentoPesoMaximoLimite;

      if (valor < pesoMinimo) {
        isValido = false;
        mensagem = `O peso deve ser maior ou igual a ${pesoMinimo}.`;
      } else if (valor > pesoMaximo) {
        isValido = false;
        mensagem = `O peso deve ser menor ou igual a ${pesoMaximo}.`;
      }
    } else if (campo === 'notaMinima') {
      const notaMinimaConfiguracao = Number((this.instrumentoConfiguracaoTurma as { notaMinima?: number } | null)?.notaMinima);
      const notaMinimaLimite = !Number.isNaN(notaMinimaConfiguracao) ? notaMinimaConfiguracao : 0;

      if (valor < 0) {
        isValido = false;
        mensagem = 'A menor nota não pode ser negativa.';
      } else if (valor < notaMinimaLimite) {
        isValido = false;
        mensagem = `A menor nota deve ser maior ou igual a ${notaMinimaLimite}.`;
      }
    } else if (campo === 'notaMaxima') {
      const notaMaximaConfiguracao = Number((this.instrumentoConfiguracaoTurma as { notaMaxima?: number } | null)?.notaMaxima);
      const notaMaximaLimite = !Number.isNaN(notaMaximaConfiguracao) ? notaMaximaConfiguracao : 100;

      if (valor < 0) {
        isValido = false;
        mensagem = 'A maior nota não pode ser negativa.';
      } else if (valor > notaMaximaLimite) {
        isValido = false;
        mensagem = `A maior nota deve ser menor ou igual a ${notaMaximaLimite}.`;
      }
    }

    if (!isValido) {
      this.instrumentoForm[campo] = '';
      this.notificationService.warning({ title: 'Campo inválido', message: mensagem });
      // Delay pra focar o campo só após o toasty renderizar
      setTimeout(() => {
        this.instrumentoModalRef?.focusField(campo);
      }, 100);
    }
  }

  // === Regras simples de edição (idênticas nos dois componentes) ===
  podeEditarInstrumento(_instrumento: InstrumentoResumoView): boolean {
    return true;
  }

  isEdicaoInstrumentoBloqueada(_instrumento: InstrumentoResumoView): boolean {
    return false;
  }

  // === Publicação ===
  protected getMensagemSucessoPublicacaoInstrumento(instrumento: InstrumentoResumoView, publicado: boolean): string {
    return publicado
      ? `O instrumento ${this.getRotuloInstrumento(instrumento)} foi publicado com sucesso para Pais e Alunos.`
      : `A publicação do instrumento ${this.getRotuloInstrumento(instrumento)} foi desfeita com sucesso.`;
  }

  protected getRotuloInstrumento(instrumento: InstrumentoResumoView): string {
    const sigla = String(instrumento.sigla || '').trim();
    const descricao = String(instrumento.descricao || '').trim();
    if (sigla && descricao) {
      return `${sigla} - ${descricao}`;
    }
    return sigla || descricao || 'selecionado';
  }

  // === Validação de datas letivas ===
  protected getPeriodoSelecionadoParaValidacaoLetiva(): { dataInicial: string; dataFinal: string } | null {
    const periodoDetalhado = this.findInstrumentoPeriodoDetalhado(this.instrumentoForm.periodoAvaliativoId);
    const dataInicial = periodoDetalhado?.dataInicial || periodoDetalhado?.periodoAvaliativo?.dataInicial || this.periodo?.dataInicial;
    const dataFinal = periodoDetalhado?.dataFinal || periodoDetalhado?.periodoAvaliativo?.dataFinal || this.periodo?.dataFinal;
    if (!dataInicial || !dataFinal) {
      return null;
    }
    return { dataInicial, dataFinal };
  }

  protected getPeriodoSelecionadoParaValidacaoLetivaRecuperacaoVinculada(): { dataInicial: string; dataFinal: string } | null {
    const instrumentoBase = this.instrumentoRecuperacaoVinculada.instrumentoBase as any;
    if (!instrumentoBase) {
      return null;
    }

    const dataInicial = instrumentoBase.periodoAvaliativo?.dataInicial || this.instrumentoPeriodoSelecionado?.dataInicial || this.periodo?.dataInicial;
    const dataFinal = instrumentoBase.periodoAvaliativo?.dataFinal || this.instrumentoPeriodoSelecionado?.dataFinal || this.periodo?.dataFinal;
    if (!dataInicial || !dataFinal) {
      return null;
    }

    return { dataInicial, dataFinal };
  }

  protected preloadInstrumentoRecuperacaoDetalhes(instrumentoId: number): void {
    if (!instrumentoId || this.instrumentoRecuperacaoDetalhes.has(instrumentoId)) {
      return;
    }

    this.instrumentoAvaliacaoService.get(instrumentoId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (detalhes) => {
          if (!detalhes) {
            return;
          }
          this.instrumentoRecuperacaoDetalhes.set(instrumentoId, detalhes);
        },
        error: () => undefined
      });
  }

  // === Campo adicional ===
  protected resetInstrumentoCampoAdicional(): void {
    this.instrumentoCampoAdicional = null;
    this.instrumentoCamposAdicionaisCarregando = false;
    this.instrumentoCamposAdicionaisObrigatoriosValidos = true;
    this.instrumentoCampoAdicionalSnapshotInicial = 'null';
    this.instrumentoCamposAdicionaisCarregadoParaId = null;
    this.instrumentoCamposAdicionaisTemplateCarregado = false;
  }

  // === Anexos ===
  protected validarInstrumentoAnexo(file: File): boolean {
    const extensao = this.getInstrumentoAnexoExtensao(file.name);
    if (!extensao || !(INSTRUMENTO_ANEXO_EXTENSOES_PERMITIDAS as readonly string[]).includes(extensao)) {
      this.notificationService.warning({ title: 'Formato não permitido', message: 'Envie arquivos PDF, DOC, DOCX, TXT, HTML, XLS, XLSX, JPG, JPEG ou PNG.' });
      return false;
    }

    if (file.size > INSTRUMENTO_ANEXO_TAMANHO_MAXIMO) {
      this.notificationService.warning({ title: 'Arquivo muito grande', message: `O arquivo ${file.name} excede o limite de 40 MB.` });
      return false;
    }

    return true;
  }

  protected getInstrumentoAnexoExtensao(fileName: string): string {
    return getInstrumentoAnexoExtensao(fileName);
  }

  // === Resumo / listagem ===
  protected toInstrumentoResumo(registro: InstrumentoAvaliacaoRegistro): InstrumentoResumoView {
    const raw = registro as unknown as Record<string, any>;
    const tipoInstrumento = (registro.tipoInstrumento as TipoInstrumentoAvaliacaoId) || 'INSTRUMENTO_AVALIACAO';
    const publicavel = (registro.publicavel || {}) as { publicado?: boolean; dataPublicacao?: string | null };
    let publicado = publicavel.publicado;

    if (typeof publicado !== 'boolean') {
      publicado = typeof registro.publicado === 'boolean' ? registro.publicado : typeof raw['publicar'] === 'boolean' ? raw['publicar'] : false;
    }

    const turmaId = Number(registro.turma?.id || 0) || this.extractTurmaIdFromRaw(raw) || this.turma?.id || null;
    return {
      id: Number(registro.id || 0),
      descricao: String(registro.descricao || ''),
      sigla: String(registro.sigla || ''),
      turmaId,
      data: registro.data,
      tipoInstrumento,
      tipoInstrumentoLabel: this.getTipoInstrumentoLabel(tipoInstrumento),
      modoAvaliacao: registro.modoAvaliacao || undefined,
      instrumentoRecuperacaoId: Number(registro.instrumentoRecuperacao?.id || 0) || null,
      periodoAvaliativoId: Number(registro.periodoAvaliativo?.id || 0) || null,
      publico: !!publicado,
      dataPublicacao: publicavel.dataPublicacao || registro.dataPublicacao || null,
      raw
    };
  }

  protected hasInstrumentoPublicacaoState(registro: InstrumentoAvaliacaoRegistro): boolean {
    const raw = registro as Record<string, any>;
    return typeof registro.publicavel?.publicado === 'boolean' || typeof registro.publicado === 'boolean' || typeof raw['publicar'] === 'boolean';
  }

  protected findInstrumentoPeriodoDetalhado(periodoId: number | null): InstrumentoPeriodoOption | undefined {
    if (!periodoId) {
      return undefined;
    }

    return this.instrumentoPeriodos.find((item) => Number(item.periodoAvaliativo?.id ?? item.id ?? 0) === periodoId);
  }

  // === Parsing utilitário ===
  protected parseOptionalNumber(value: string): number | null {
    return parseOptionalNumber(value);
  }

  protected parseDate(value: unknown): Date | null {
    return parseDate(value);
  }
}
